import { Button, SimplePagination, Spinner } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import type { NumberType } from 'lib-shared-legacy-types';
import { PureComponent, ReactNode } from 'react';

import type { WithPhoneSearchInjectedProps } from 'hocs/telephony';

import styles from './customTab.module.scss';
import ListGroups from './listGroups';
import ListNumbers from './listNumbers';
import type { BuyNumberQueryT } from './modals/buyNumber';

type PropsT = {
  type: NumberType;
  title: string;
  buyNumber: (query: BuyNumberQueryT) => void;
} & WithPhoneSearchInjectedProps;

export default class CustomTab extends PureComponent<PropsT> {
  get results() {
    const {
      type,
      phoneSearch: { items },
    } = this.props;

    return items[type];
  }

  @boundMethod
  handleNext() {
    const {
      phoneSearch: { nextPage },
      type,
    } = this.props;

    nextPage(type);
  }

  @boundMethod
  handlePrev() {
    const {
      phoneSearch: { previousPage },
      type,
    } = this.props;

    previousPage(type);
  }

  renderLoadingStatus() {
    const { type, phoneSearch } = this.props;
    const { getItemsAsyncStatus, getItems } = phoneSearch;
    const { numbers, groups, country } = this.results || {};

    if (
      getItemsAsyncStatus === 'succeeded' &&
      numbers?.length === 0 &&
      groups?.length === 0 &&
      country
    ) {
      return (
        <div>
          <span className="text-center">
            No {type === 'geographic' ? 'local' : type} numbers found{' '}
            <strong>
              in{' '}
              <strong className="text-capitalize">
                {country.country.toLowerCase()}
              </strong>
            </strong>
            . Contact Dixa if you need a number.
          </span>
        </div>
      );
    }

    if (getItemsAsyncStatus === 'loading') {
      return (
        <div className={styles.loading}>
          <Spinner />
        </div>
      );
    }

    if (getItemsAsyncStatus === 'failed') {
      return (
        <div className="row-item telephony__empty">
          <span>Error while fetching numbers...</span>
          <Button
            type="button"
            variant="tertiary"
            onClick={() => getItems({ country }, [type])}
          >
            Try again
          </Button>
        </div>
      );
    }

    return null;
  }

  renderPaginationButtons(): ReactNode {
    const results = this.results;
    const { phoneSearch } = this.props;

    return (
      <SimplePagination
        previousButtonClick={this.handlePrev}
        previousButtonDisabled={!results?.previousPagePath}
        nextButtonClick={this.handleNext}
        nextButtonDisabled={!results?.nextPagePath}
        hidden={phoneSearch.getItemsAsyncStatus === 'loading'}
      />
    );
  }

  renderNumbers() {
    const { buyNumber } = this.props;
    const { numbers, country } = this.results || {};

    if (!numbers || !country) {
      return null;
    }

    return numbers.map((number) => (
      <ListNumbers
        key={number.phone_number.e164_format}
        number={number}
        country={country}
        showBuyNumberModal={buyNumber}
      />
    ));
  }

  renderGroups() {
    const { buyNumber } = this.props;
    const { groups, country } = this.results || {};

    if (!groups || !country) {
      return null;
    }

    return groups.map((group, index) => (
      <ListGroups
        key={index}
        group={group}
        country={country}
        showBuyNumberModal={buyNumber}
      />
    ));
  }

  render() {
    const {
      phoneSearch: { getItemsAsyncStatus },
    } = this.props;
    const isLoading = getItemsAsyncStatus !== 'succeeded';

    return (
      <div className={styles.wrapper}>
        <div className={styles.list}>
          {this.renderLoadingStatus()}
          {!isLoading && this.renderNumbers()}
          {!isLoading && this.renderGroups()}
        </div>
        <div className={styles.buttons}>{this.renderPaginationButtons()}</div>
      </div>
    );
  }
}
